import React from "react";
import { useNavigate } from "react-router-dom";
import useChatThreads from "../hooks/useChatThreads";
import "../assets/css/chat.scss";
import Breadcrumbs from "../components/Breadcrumbs";

const ChatInbox = () => {
  const sessionUserId = sessionStorage.getItem("id");
  const storedRole = sessionStorage.getItem("chatInboxRole");
  const [viewRole, setViewRole] = React.useState(
    storedRole === "buyer" || storedRole === "seller" ? storedRole : "seller"
  );
  const isBuyerView = viewRole === "buyer";
  const navigate = useNavigate();
  const { threads, loading, error, refresh, summary } = useChatThreads({
    userId: sessionUserId,
    role: viewRole,
  });

  React.useEffect(() => {
    sessionStorage.setItem("chatInboxRole", viewRole);
  }, [viewRole]);

  const roleOptions = [
    {
      key: "seller",
      title: "Seller view",
      description: "See buyers who reached out to your listings.",
    },
    {
      key: "buyer",
      title: "Buyer view",
      description: "See sellers you contacted about a product.",
    },
  ];

  const handleOpenThread = (thread) => {
    const productId = thread.productId;
    const buyerId = isBuyerView ? sessionUserId : thread.buyerId;
    const sellerId = isBuyerView ? thread.sellerId : sessionUserId;
    if (!productId || !buyerId || !sellerId) return;
    const roomId = thread.roomId || `chat_${productId}_${buyerId}_${sellerId}`;
    navigate(`/chat/${roomId}`, {
      state: {
        productId,
        buyerId,
        sellerId,
        productName: thread.productName,
        buyerName: thread.buyerName,
        sellerName: thread.sellerName,
      },
    });
  };

  const formatRelative = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "";
    const diff = Date.now() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="chat-layout">
      <Breadcrumbs title="Chat Inbox" items={[{ label: "Dashboard", path: "/" }, { label: "Chat", path: "#" }]} />
      <div className="chat-card">
        <div className="chat-inbox__header">
          <div>
            <h2>Conversations</h2>
          </div>
          <button className="btn btn-outline-primary" onClick={refresh} disabled={loading}>
            Refresh
          </button>
        </div>

        <div className="chat-inbox__tabs mb-4">
          {roleOptions.map((option) => {
            const isActive = option.key === viewRole;
            return (
              <button
                key={option.key}
                type="button"
                className={`chat-role-pill ${isActive ? "is-active" : ""}`}
                onClick={() => setViewRole(option.key)}
              >
                <span className="chat-role-pill__title">{option.title}</span>
                <span className="chat-role-pill__desc">{option.description}</span>
              </button>
            );
          })}
        </div>

        <div className="chat-inbox__stats mb-4">
          <div className="chat-stat">
            <span className="chat-stat__label">Active threads</span>
            <span className="chat-stat__value">{summary.totalThreads}</span>
          </div>
          <div className="chat-stat">
            <span className="chat-stat__label">Unread</span>
            <span className="chat-stat__value">{summary.totalUnread}</span>
          </div>
        </div>

        {loading && <p className="text-muted mt-3">Loading conversations…</p>}
        {error && (
          <div className="alert alert-danger mt-3">
            Unable to load chats. Please try again later.
          </div>
        )}

        {!loading && threads.length === 0 && (
          <div className="chat-empty">
            <p>
              {isBuyerView
                ? "No conversations yet. Sellers will appear here when they reply."
                : "No conversations yet. Buyers will appear here when they send a message."}
            </p>
          </div>
        )}

        <ul className="chat-inbox__list">
          {threads.map((thread) => {
            const counterpartLabel = isBuyerView
              ? thread.sellerName || (thread.sellerId ? `Seller #${thread.sellerId}` : "Seller")
              : thread.buyerName || (thread.buyerId ? `Buyer #${thread.buyerId}` : "Buyer");
            return (
              <li key={thread.roomId || `${thread.productId}-${thread.buyerId || thread.sellerId}`}>
                <div className="chat-thread" onClick={() => handleOpenThread(thread)}>
                  <div>
                    <p className="chat-thread__title">{thread.productName || "Product"}</p>
                    <div className="chat-thread__badges">
                      <span className="badge rounded-pill bg-light text-dark">{counterpartLabel}</span>
                      {/* <span className="badge rounded-pill bg-light text-dark">
                        {isBuyerView ? "Seller" : "Buyer"} view
                      </span> */}
                    </div>
                    <p className="chat-thread__subtitle">
                      {thread.lastMessage || "Tap to open conversation"}
                    </p>
                  </div>
                  <div className="chat-thread__meta">
                    <span>{formatRelative(thread.lastMessageAt)}</span>
                    {thread.unreadCount > 0 && (
                      <span className="chat-thread__unread">{thread.unreadCount}</span>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default ChatInbox;

